import React from 'react';
import { Globe, Briefcase, Heart, Code2, Mountain, Music4, Tent, Fish } from 'lucide-react';
import { motion } from 'framer-motion';

const Personality = () => {
  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } }
  };

  const stagger = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const hobbies = [
    { name: 'Football', icon: <Heart className="w-4 h-4" /> },
    { name: 'Cycling', icon: <Mountain className="w-4 h-4" /> },
    { name: 'Coding', icon: <Code2 className="w-4 h-4" /> },
    { name: 'Camping', icon: <Tent className="w-4 h-4" /> },
    { name: 'Fishing', icon: <Fish className="w-4 h-4" /> },
    { name: 'Traveling', icon: <Globe className="w-4 h-4" /> },
  ];

  const languages = [
    { name: 'Arabic', level: 'Native' },
    { name: 'English', level: 'Professional' },
    { name: 'French', level: 'Intermediate' },
  ];

  const preferences = [
    'Remote-friendly: Comfortable collaborating across timezones',
    'Relocation: Open to relocating for the right opportunity',
    'Timezone: GMT+1 (Flexible working hours)',
  ];

  return (
    <section className="py-20 bg-white dark:bg-black">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Section header */}
        <motion.div
          className="text-center mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <h2 className="text-3xl text-gray-900 dark:text-white mb-3 flex items-center justify-center gap-2">
            <Heart className="w-7 h-7 text-indigo-500 dark:text-indigo-400" />
            Personality Snapshot
          </h2>
          <div className="w-16 h-px bg-gray-300 dark:bg-gray-700 mx-auto" />
          <p className="text-gray-600 dark:text-gray-400 max-w-5xl mx-auto text-base font-light leading-relaxed mt-2">
            A glimpse into the core values, mindset, and soft skills that drive my professional journey and collaboration style.
          </p>
        </motion.div>

        {/* Three‑column grid with premium cards */}
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-6"
        >
          {/* Hobbies & Interests */}
          <motion.div variants={fadeInUp} className="group">
            <div className="relative h-full">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl blur opacity-20 group-hover:opacity-30 transition" />
              <div className="relative h-full bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-800">
                <h3 className="text-sm font-medium text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Music4 className="w-4 h-4" />
                  Hobbies & Interests
                </h3>
                <div className="flex flex-wrap gap-x-3 gap-y-2">
                  {hobbies.map((hobby, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300">
                      <span className="text-gray-400 dark:text-gray-500">{hobby.icon}</span>
                      {hobby.name}
                      {i < hobbies.length - 1 && (
                        <span className="text-gray-300 dark:text-gray-600 mx-1">•</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Languages */}
          <motion.div variants={fadeInUp} className="group">
            <div className="relative h-full">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl blur opacity-20 group-hover:opacity-30 transition" />
              <div className="relative h-full bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-800">
                <h3 className="text-sm font-medium text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Languages
                </h3>
                <ul className="space-y-3">
                  {languages.map((lang, i) => (
                    <li key={i} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700 dark:text-gray-300">{lang.name}</span>
                      <span className="text-xs px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-600 dark:text-indigo-400 rounded-full border border-indigo-200 dark:border-indigo-800">
                        {lang.level}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Work Preferences */}
          <motion.div variants={fadeInUp} className="group">
            <div className="relative h-full">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl blur opacity-20 group-hover:opacity-30 transition" />
              <div className="relative h-full bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-800">
                <h3 className="text-sm font-medium text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Work Preferences
                </h3>
                <ul className="space-y-3">
                  {preferences.map((pref, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <span className="text-indigo-400 mt-0.5">•</span>
                      <span>{pref}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Personality;